import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  api,
  type PortalPaymentItem,
  type PortalProfile,
  type PortalVpnKeyConfig,
  type PortalVpnKeyItem,
} from '../services/api';
import { formatDate, formatMoney, subStatusClass, subStatusLabel } from '../utils/format';

type Tab = 'overview' | 'vpn' | 'payments' | 'promo';

export function CabinetPage() {
  const { user, logout, refresh } = useAuth();
  const [tab, setTab] = useState<Tab>('overview');
  const [profile, setProfile] = useState<PortalProfile | null>(null);
  const [keys, setKeys] = useState<PortalVpnKeyItem[]>([]);
  const [payments, setPayments] = useState<PortalPaymentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [promoCode, setPromoCode] = useState('');
  const [promoMsg, setPromoMsg] = useState<string | null>(null);
  const [promoErr, setPromoErr] = useState<string | null>(null);
  const [config, setConfig] = useState<PortalVpnKeyConfig | null>(null);
  const [busyKeyId, setBusyKeyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [prof, vpn, pays] = await Promise.all([
      api.getProfile(),
      api.listVpnKeys(),
      api.listPayments(),
    ]);
    if (prof.data) setProfile(prof.data);
    if (vpn.data) setKeys(vpn.data);
    if (pays.data) setPayments(pays.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadConfig = async (keyId: number) => {
    setBusyKeyId(keyId);
    setConfig(null);
    const res = await api.getVpnConfig(keyId);
    if (res.data) setConfig(res.data);
    setBusyKeyId(null);
  };

  const downloadConfig = () => {
    if (!config) return;
    const blob = new Blob([config.config_text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `yourprov-${config.server_code}.conf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const redeemPromo = async (e: React.FormEvent) => {
    e.preventDefault();
    setPromoMsg(null);
    setPromoErr(null);
    const res = await api.redeemPromo(promoCode.trim());
    if (res.data) {
      setPromoMsg(
        `Промокод ${res.data.code}: +${res.data.bonus_days} дн., tier ${res.data.subscription_tier}, до ${formatDate(res.data.subscription_until)}`,
      );
      setPromoCode('');
      await refresh();
      await load();
    } else if (res.error) {
      setPromoErr(res.error.message);
    }
  };

  return (
    <div>
      <div className="header-bar">
        <div>
          <h1 style={{ margin: 0 }}>Личный кабинет</h1>
          <p className="muted" style={{ margin: '0.25rem 0 0' }}>
            {user?.first_name ?? user?.username ?? user?.telegram_user_id}
          </p>
        </div>
        <button type="button" className="btn btn-ghost" onClick={() => logout()}>
          Выйти
        </button>
      </div>

      <div className="tabs">
        {(['overview', 'vpn', 'payments', 'promo'] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            className={`tab ${tab === t ? 'active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'overview' && 'Подписка'}
            {t === 'vpn' && `VPN (${keys.length})`}
            {t === 'payments' && `Платежи (${payments.length})`}
            {t === 'promo' && 'Промокод'}
          </button>
        ))}
      </div>

      {loading && <p className="muted">Загрузка…</p>}

      {!loading && tab === 'overview' && profile && (
        <div className="grid-2">
          <div className="card">
            <div className="muted">Подписка</div>
            <div style={{ marginTop: '0.5rem' }}>
              <span className={subStatusClass(profile.subscription.subscription_status)}>
                {subStatusLabel(profile.subscription.subscription_status)}
              </span>
            </div>
          </div>
          <div className="card">
            <div className="muted">Tier</div>
            <div style={{ marginTop: '0.5rem' }}>{profile.subscription.subscription_tier ?? '—'}</div>
          </div>
          <div className="card">
            <div className="muted">Действует до</div>
            <div style={{ marginTop: '0.5rem' }}>
              {formatDate(profile.subscription.subscription_until)}
            </div>
          </div>
          <div className="card">
            <div className="muted">VPN ключей</div>
            <div style={{ marginTop: '0.5rem', fontSize: '1.5rem', fontWeight: 600 }}>
              {profile.subscription.active_keys_count}
            </div>
          </div>
        </div>
      )}

      {!loading && tab === 'vpn' && (
        <div>
          {keys.length === 0 ? (
            <p className="muted">Активных ключей нет. Создайте ключ через Telegram-бота.</p>
          ) : (
            keys.map((k) => (
              <div key={k.id} className="card" style={{ marginBottom: '0.75rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                  <div>
                    <strong>{k.server_country.toUpperCase()}</strong>
                    <div className="muted" style={{ fontSize: '0.8rem' }}>
                      {k.client_id.slice(0, 16)}… · {formatDate(k.created_at)}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={busyKeyId === k.id}
                    onClick={() => loadConfig(k.id)}
                  >
                    {busyKeyId === k.id ? 'Загрузка…' : 'Конфиг'}
                  </button>
                </div>
                {config?.key_id === k.id && (
                  <div style={{ marginTop: '1rem' }}>
                    <button type="button" className="btn btn-ghost" onClick={downloadConfig}>
                      Скачать .conf
                    </button>
                    <pre className="config-block" style={{ marginTop: '0.75rem' }}>
                      {config.config_text}
                    </pre>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      {!loading && tab === 'payments' && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Сумма</th>
                <th>Дней</th>
                <th>Tier</th>
                <th>Статус</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted">
                    Платежей пока нет
                  </td>
                </tr>
              )}
              {payments.map((p) => (
                <tr key={p.id}>
                  <td>{formatDate(p.created_at)}</td>
                  <td>{formatMoney(p.amount)}</td>
                  <td>{p.days}</td>
                  <td>{p.subscription_tier ?? '—'}</td>
                  <td>{p.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!loading && tab === 'promo' && (
        <div className="card" style={{ maxWidth: 420 }}>
          <h3 style={{ marginTop: 0 }}>Активировать промокод</h3>
          <form onSubmit={redeemPromo} style={{ display: 'flex', gap: '0.5rem' }}>
            <input
              className="input"
              placeholder="CODE"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              required
              style={{ flex: 1 }}
            />
            <button type="submit" className="btn btn-primary">
              Применить
            </button>
          </form>
          {promoMsg && <p style={{ color: 'var(--success)', marginTop: '0.75rem' }}>{promoMsg}</p>}
          {promoErr && <p className="error-text" style={{ marginTop: '0.75rem' }}>{promoErr}</p>}
        </div>
      )}
    </div>
  );
}
